import { useCallback, useEffect, useImperativeHandle, useRef, useState, forwardRef } from "react";
import type { Point, Shape } from "@coaching/types";
import { fitContain, lengthToFrame, toFrame, toPixels, type FrameBox } from "@coaching/annotations";
import type { ToolState } from "./Toolbar";
import { createPlayer, type Player } from "./player";

export interface StageHandle {
    seek(t: number): void;
    togglePlay(): void;
    pause(): void;
    /** Whole frames from the frame on screen. */
    step(frames: number): void;
    /** Seconds from the playhead, for the coarse buttons. */
    nudge(sec: number): void;
    /** The time to store for a moment on the frame showing now. */
    frameTime(): number;
}

interface Props {
    /** The playlist of the clip to show. */
    src: string;
    /** Changes with the clip; a change loads `src` from `startAt`. */
    clipKey: string;
    startAt: number;
    autoplay: boolean;
    /** Bumped when the signatures are refreshed: the same clip is reloaded at the same position. */
    generation: number;
    frameWidth: number | null;
    frameHeight: number | null;
    shapes: Shape[];
    tool: ToolState;
    canDraw: boolean;
    onTime: (t: number, paused: boolean) => void;
    onDurationKnown: (sec: number) => void;
    onEnded: () => void;
    onShape: (shape: Shape) => void;
    onMediaError: () => void;
}

interface Drawing {
    pointerId: number;
    pointerType: string;
    start: Point;
    last: Point;
    points: Point[];
}

const PEN_QUIET_MS = 2500;
const MIN_POINT_GAP = 1.5;

/**
 * The video with the drawing surface over it. Every shape is stored in frame
 * space and rendered through the same fit box the app uses, so what the coach
 * draws here is what the player sees there. Pen input wins over touch: while
 * a pen has been seen recently, touches are treated as a resting hand.
 */
export const VideoStage = forwardRef<StageHandle, Props>(function VideoStage(props, ref) {
    const { src, clipKey, startAt, autoplay, generation, frameWidth, frameHeight, shapes, tool, canDraw, onTime, onDurationKnown, onEnded, onShape, onMediaError } = props;
    const videoRef = useRef<HTMLVideoElement>(null);
    const stageRef = useRef<HTMLDivElement>(null);
    const player = useRef<Player | null>(null);
    const [stageSize, setStageSize] = useState({ w: 0, h: 0 });
    const [nativeSize, setNativeSize] = useState<{ w: number; h: number } | null>(null);
    // The live stroke is a ref, not state: pointer moves arrive faster than React
    // re-renders and every point must be kept. State only asks for a repaint.
    const drawingRef = useRef<Drawing | null>(null);
    const [, repaint] = useState(0);
    const paint = () => repaint((n) => n + 1);
    const penSeenAt = useRef(0);
    const activeTouches = useRef(0);
    const loaded = useRef<{ clipKey: string; generation: number } | null>(null);
    const latestProps = useRef({ src, startAt, autoplay, onMediaError });
    latestProps.current = { src, startAt, autoplay, onMediaError };

    useEffect(() => {
        const el = stageRef.current;
        if (!el) return;
        const ro = new ResizeObserver(([entry]) => {
            const r = entry.contentRect;
            setStageSize({ w: r.width, h: r.height });
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        player.current = createPlayer(v, () => latestProps.current.onMediaError());
        return () => { player.current?.destroy(); player.current = null; loaded.current = null; };
    }, []);

    // A new clip loads from its start time; a refreshed signature reloads the same
    // clip where it was, playing if it was playing.
    useEffect(() => {
        const p = player.current;
        const v = videoRef.current;
        if (!p || !v) return;
        const prev = loaded.current;
        if (prev && prev.clipKey === clipKey && prev.generation === generation) return;
        const sameClip = prev?.clipKey === clipKey;
        const at = sameClip ? v.currentTime : startAt;
        const play = sameClip ? !v.paused : autoplay;
        loaded.current = { clipKey, generation };
        p.load(src, { startAt: at, autoplay: play });
    }, [src, clipKey, generation, startAt, autoplay]);

    const report = useCallback(() => {
        const v = videoRef.current;
        const p = player.current;
        if (!v || !p) return;
        onTime(v.paused ? p.frameTime() : v.currentTime, v.paused);
    }, [onTime]);

    const onLoaded = useCallback(() => {
        const v = videoRef.current;
        if (!v) return;
        if (v.videoWidth && v.videoHeight) setNativeSize({ w: v.videoWidth, h: v.videoHeight });
        if (Number.isFinite(v.duration)) onDurationKnown(v.duration);
    }, [onDurationKnown]);

    useImperativeHandle(ref, () => ({
        seek(t) { player.current?.seek(t); },
        togglePlay() { const v = videoRef.current; if (!v) return; if (v.paused) void v.play().catch(() => undefined); else v.pause(); },
        pause() { videoRef.current?.pause(); },
        step(frames) { player.current?.step(frames); },
        nudge(sec) { const v = videoRef.current; if (v) { v.pause(); player.current?.seek(v.currentTime + sec); } },
        frameTime() { return player.current?.frameTime() ?? videoRef.current?.currentTime ?? 0; },
    }), []);

    // timeupdate fires only a few times a second; a frame loop keeps the scrubber smooth while playing.
    useEffect(() => {
        let raf = 0;
        const tick = () => {
            const v = videoRef.current;
            if (v && !v.paused) onTime(v.currentTime, false);
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [onTime]);

    const fw = frameWidth ?? nativeSize?.w ?? 16;
    const fh = frameHeight ?? nativeSize?.h ?? 9;
    const box: FrameBox = fitContain(fw, fh, stageSize.w, stageSize.h);

    const toShape = (d: Drawing): Shape | null => {
        const { color, width } = tool;
        const a = toFrame(d.start, box);
        const b = toFrame(d.last, box);
        switch (tool.kind) {
        case "path":
            return d.points.length >= 2 ? { kind: "path", points: d.points.map((p) => toFrame(p, box)), color, width } : null;
        case "line":
            return { kind: "line", x1: a.x, y1: a.y, x2: b.x, y2: b.y, color, width };
        case "arrow":
            return { kind: "arrow", x1: a.x, y1: a.y, x2: b.x, y2: b.y, color, width };
        case "circle": {
            const r = lengthToFrame(Math.hypot(d.last.x - d.start.x, d.last.y - d.start.y), box);
            return r > 0.002 ? { kind: "circle", cx: a.x, cy: a.y, r: Math.min(r, 1), color, width } : null;
        }
        case "rect":
            return { kind: "rect", x: a.x, y: a.y, w: b.x - a.x, h: b.y - a.y, color, width };
        }
    };

    const local = (e: React.PointerEvent): Point => {
        const r = stageRef.current?.getBoundingClientRect();
        return r ? { x: e.clientX - r.left, y: e.clientY - r.top } : { x: 0, y: 0 };
    };

    const onPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
        if (!canDraw || box.width === 0) return;
        const drawing = drawingRef.current;
        if (e.pointerType === "touch") {
            activeTouches.current += 1;
            if (Date.now() - penSeenAt.current < PEN_QUIET_MS || activeTouches.current > 1 || drawing) return;
        } else if (e.pointerType === "pen") {
            penSeenAt.current = Date.now();
            if (drawing && drawing.pointerType === "touch") drawingRef.current = null;
        } else if (e.button !== 0) {
            return;
        }
        e.preventDefault();
        (e.target as Element).setPointerCapture(e.pointerId);
        videoRef.current?.pause();
        const p = local(e);
        drawingRef.current = { pointerId: e.pointerId, pointerType: e.pointerType, start: p, last: p, points: [p] };
        paint();
    };

    const onPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
        const d = drawingRef.current;
        if (!d || e.pointerId !== d.pointerId) return;
        const p = local(e);
        d.last = p;
        if (tool.kind === "path") {
            const prev = d.points[d.points.length - 1];
            if (Math.hypot(p.x - prev.x, p.y - prev.y) >= MIN_POINT_GAP) d.points.push(p);
        }
        paint();
    };

    const finish = (e: React.PointerEvent<SVGSVGElement>, cancelled: boolean) => {
        if (e.pointerType === "touch") activeTouches.current = Math.max(0, activeTouches.current - 1);
        const d = drawingRef.current;
        if (!d || e.pointerId !== d.pointerId) return;
        drawingRef.current = null;
        paint();
        if (cancelled) return;
        const shape = toShape(d);
        if (shape) onShape(shape);
    };

    const live = drawingRef.current ? toShape(drawingRef.current) : null;
    const rendered = live ? [...shapes, live] : shapes;

    return (
        <div ref={stageRef} className={`stage ${canDraw ? "drawable" : ""}`}>
            <video ref={videoRef} playsInline preload="metadata" controls={false} crossOrigin="anonymous"
                onLoadedMetadata={onLoaded}
                onPause={report}
                onPlay={report}
                onSeeked={report}
                onEnded={onEnded} />
            <svg className="overlay" width={stageSize.w} height={stageSize.h} viewBox={`0 0 ${stageSize.w} ${stageSize.h}`}
                onPointerDown={onPointerDown} onPointerMove={onPointerMove}
                onPointerUp={(e) => finish(e, false)} onPointerCancel={(e) => finish(e, true)} onPointerLeave={(e) => finish(e, false)}>
                {box.width > 0 && rendered.map((s, i) => <RenderShape key={i} shape={s} box={box} />)}
            </svg>
        </div>
    );
});

function RenderShape({ shape, box }: { shape: Shape; box: FrameBox }) {
    const px = toPixels(shape, box);
    const common = { stroke: px.color, strokeWidth: px.strokeWidth, fill: "none", strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
    switch (px.kind) {
    case "path":
        return <path d={px.points.map((p, i) => `${i ? "L" : "M"}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ")} {...common} />;
    case "line":
        return <line x1={px.from.x} y1={px.from.y} x2={px.to.x} y2={px.to.y} {...common} />;
    case "arrow":
        return (
            <g>
                <line x1={px.from.x} y1={px.from.y} x2={px.to.x} y2={px.to.y} {...common} />
                <polyline points={px.head.map((p) => `${p.x},${p.y}`).join(" ")} {...common} />
            </g>
        );
    case "circle":
        return <circle cx={px.centre.x} cy={px.centre.y} r={px.radius} {...common} />;
    case "rect":
        return <rect x={px.origin.x} y={px.origin.y} width={px.width} height={px.height} rx={2} {...common} />;
    }
}
